import { useCallback, useState } from 'react';
import { RefreshCcw } from 'lucide-react';
import { useVisionCanvas } from '@/context/vision-canvas-context';
import { EngineCommand } from '@akavirtual/deai-web-core';


type StartStateProps = {
  onStart: () => void;
  isConnected: boolean;
};

type ResetStateProps = {
  onReset: () => void;
};

type Props = {
  soundURL: string;
  onStart?: () => void;
  onReset?: () => void;
  StartState?: React.ComponentType<StartStateProps>;
  ResetState?: React.ComponentType<ResetStateProps>;
};

const DefaultStartState =({ onStart, isConnected }:StartStateProps) => (
  <div className="absolute h-full w-full flex items-center justify-center bg-black/75 backdrop-blur-sm">
    <button
      onClick={onStart}
      disabled={!isConnected}
      className="flex h-48 w-48 items-center justify-center rounded-full border-2 border-cyan-400 bg-cyan-500 text-white shadow-[0_0_24px_2px_#06b6d4] cursor-pointer text-3xl font-bold transition-all duration-200 hover:scale-105 hover:shadow-[0_0_32px_4px_#06b6d4] disabled:cursor-not-allowed disabled:bg-gray-500 disabled:border-gray-400 disabled:shadow-none"
    >
      <span>{isConnected ? 'BEGIN' : 'WAITING'}</span>
    </button>
  </div>
);

const DefaultResetState = ({ onReset }:ResetStateProps)  => (
  <button
    onClick={onReset}
    aria-label="Reset Session"
    className="absolute top-5 left-5 z-50 p-3 bg-black/20 text-white backdrop-blur-sm rounded-full transition-colors hover:bg-white/40"
  >
    <RefreshCcw className="h-6 w-6" />
  </button>);


export const StartOverlay = ({
  soundURL,
  onStart: onStartProp,
  onReset: onResetProp,
  StartState = DefaultStartState,
  ResetState = DefaultResetState,
}: Props) => {
  // const [isStarted, setIsStarted] = useState(false);
  const { broadcast, reset, isGameReady } = useVisionCanvas(); 
  const start = ()=>{
    // setIsStarted(true);
    broadcast(EngineCommand.GAME_STARTED);
  };

  const handleStart = useCallback(() => {
    try {
      new Audio(soundURL).play();
    } catch (error) {
      console.error('Audio playback failed:', error);
    }

    setTimeout(() => {
      if (onStartProp) {
        onStartProp();
      } else {
        start();
      }
    }, 300);
  }, [onStartProp, soundURL, start]);

  const handleReset = useCallback(() => {
    if (onResetProp) {
      onResetProp();
    } else {
      reset(); 
      // setIsStarted(false); 
    }
  }, [onResetProp, reset]);

  return isGameReady ? (
    <ResetState onReset={handleReset} />
  ) : (
    <StartState isConnected={true} onStart={handleStart} />
  );
};
